import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import StatusBadge from '../../../components/StatusBadge';
import Toast from '../../../components/Toast';
import Spinner from '../../../components/Spinner';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import ImageUpload from '../../../components/ImageUpload';
import ProductService from '../../../services/product.service';
import TenantService from '../../../services/tenant.service';
import ProductBrandingService, {
  ProductBrandingFormPayload,
  ProductBrandingScope,
  ProductBrandingRow,
} from '../../../services/product-branding.service';
import { API_URL } from '../../../utils/apiConstant';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { SortOrder } from '../../../enums';

const HEX_COLOUR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_COLOUR = '#0f172a';

interface ProductOption {
  id: number;
  product_name: string;
}

interface TenantOption {
  id: number;
  tenant_name: string;
  tenant_code: string;
  product_id: number;
}

interface BrandingFormState {
  productId: number | '';
  tenantId: number | '';
  logo_url: string;
  theme_colour: string;
  display_name: string;
  notes: string;
}

const initialForm: BrandingFormState = {
  productId: '',
  tenantId: '',
  logo_url: '',
  theme_colour: DEFAULT_COLOUR,
  display_name: '',
  notes: '',
};

const BrandingForm: React.FC = () => {
  const { t } = useTranslation();

  const [scope, setScope] = useState<ProductBrandingScope>('product');
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [formData, setFormData] = useState<BrandingFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [usingFallback, setUsingFallback] = useState<'product' | 'global' | null>(null);
  const [listRefreshToken, setListRefreshToken] = useState(0);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    TenantService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setTenants(response?.data?.items ?? []);
    });
  }, []);

  const referenceId = scope === 'product' ? formData.productId : formData.tenantId;

  useEffect(() => {
    if (!referenceId) {
      setFormData((prev) => ({ ...initialForm, productId: prev.productId, tenantId: prev.tenantId }));
      setUsingFallback(null);
      return;
    }

    setFetching(true);
    const request =
      scope === 'product'
        ? ProductBrandingService.getForProduct(referenceId)
        : ProductBrandingService.getForProduct(
            tenants.find((tenant) => tenant.id === referenceId)?.product_id ?? referenceId,
            referenceId
          );
    request
      .then((response: any) => {
        const resolved = response?.data;
        setUsingFallback(resolved?.source === 'global' ? 'global' : resolved?.source === 'product' && scope === 'tenant' ? 'product' : null);
        setFormData((prev) => ({
          ...prev,
          logo_url: resolved?.logoUrl ?? '',
          theme_colour: HEX_COLOUR_REGEX.test(resolved?.themeColour ?? '') ? resolved.themeColour : DEFAULT_COLOUR,
          display_name: resolved?.displayName ?? '',
          notes: '',
        }));
      })
      .finally(() => setFetching(false));
  }, [scope, referenceId]);

  const validate = useCallback(
    (data: BrandingFormState) => {
      const nextErrors: Record<string, string> = {};
      if (scope === 'product' && !data.productId) {
        nextErrors.productId = t('system.branding.pageTexts.fields.product.errors.required');
      }
      if (scope === 'tenant' && !data.tenantId) {
        nextErrors.tenantId = t('system.branding.pageTexts.fields.tenant.errors.required');
      }
      if (!data.theme_colour || !HEX_COLOUR_REGEX.test(data.theme_colour)) {
        nextErrors.theme_colour = t('system.branding.pageTexts.fields.themeColour.errors.invalid');
      }
      return nextErrors;
    },
    [t, scope]
  );

  const handleChange = useCallback((e: any, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  }, []);

  const handleLogoUploaded = useCallback((fileName: string) => {
    setFormData((prev) => ({ ...prev, logo_url: fileName }));
  }, []);

  const handleScopeChange = useCallback((next: ProductBrandingScope) => {
    setScope(next);
    setErrors({});
    setFormData(initialForm);
    setUsingFallback(null);
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: ProductBrandingFormPayload = {
          logo_url: formData.logo_url || undefined,
          theme_colour: formData.theme_colour,
          display_name: formData.display_name || undefined,
          notes: formData.notes || undefined,
        };
        if (scope === 'product') {
          await ProductBrandingService.upsert(formData.productId as number, payload);
        } else {
          await ProductBrandingService.upsertForTenant(formData.tenantId as number, payload);
        }
        toast.success(<Toast message={t('system.branding.pageTexts.saveSuccess')} />, { icon: false });
        setUsingFallback(null);
        setListRefreshToken((prev) => prev + 1);
      } catch (err) {
      }
      setLoading(false);
    },
    [formData, validate, t, scope]
  );

  const selectedProduct = products.find((p) => p.id === formData.productId);
  const selectedTenant = tenants.find((tn) => tn.id === formData.tenantId);
  const previewName =
    formData.display_name || (scope === 'product' ? selectedProduct?.product_name : selectedTenant?.tenant_name) || '';
  const previewColour = HEX_COLOUR_REGEX.test(formData.theme_colour) ? formData.theme_colour : DEFAULT_COLOUR;
  const previewLogoUrl = formData.logo_url ? `${API_URL}/uploads/product-logos/${formData.logo_url}` : undefined;
  const fallbackNoticeKey =
    usingFallback === 'global'
      ? 'system.branding.pageTexts.fallbackNotice'
      : usingFallback === 'product'
      ? 'system.branding.pageTexts.tenantFallbackNotice'
      : null;

  return (
    <div className="obs-table-card card mb-6">
      <h5 className="card-header">{t('system.branding.pageTexts.title')}</h5>
      <hr className="m-0" style={{ borderColor: 'var(--line2)' }} />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <label className="form-label d-block text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.16em', color: 'var(--dim)', fontFamily: 'var(--font-sans)' }}>
            {t('system.branding.pageTexts.fields.scope.label')}
          </label>
          <div className="d-flex gap-4 pt-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="scope"
                id="scope-product"
                checked={scope === 'product'}
                onChange={() => handleScopeChange('product')}
              />
              <label className="form-check-label" htmlFor="scope-product">
                {t('system.branding.pageTexts.fields.scope.product')}
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="scope"
                id="scope-tenant"
                checked={scope === 'tenant'}
                onChange={() => handleScopeChange('tenant')}
              />
              <label className="form-check-label" htmlFor="scope-tenant">
                {t('system.branding.pageTexts.fields.scope.tenant')}
              </label>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          {scope === 'product' ? (
            <Dropdown
              id="productId"
              name="productId"
              label={t('system.branding.pageTexts.fields.product.label')}
              placeholder={t('system.branding.pageTexts.fields.product.placeholder')}
              value={formData.productId}
              error={errors.productId}
              options={products.map((p) => ({ id: String(p.id), name: p.product_name }))}
              noSearch
              onChange={(e) => setFormData((prev) => ({ ...prev, productId: e.target.value ? Number(e.target.value) : '' }))}
            />
          ) : (
            <Dropdown
              id="tenantId"
              name="tenantId"
              label={t('system.branding.pageTexts.fields.tenant.label')}
              placeholder={t('system.branding.pageTexts.fields.tenant.placeholder')}
              value={formData.tenantId}
              error={errors.tenantId}
              options={tenants.map((tn) => ({ id: String(tn.id), name: `${tn.tenant_name} (${tn.tenant_code})` }))}
              noSearch
              onChange={(e) => setFormData((prev) => ({ ...prev, tenantId: e.target.value ? Number(e.target.value) : '' }))}
            />
          )}
        </div>

        {fetching && (
          <div className="col-12 text-center">
            <Spinner />
          </div>
        )}

        {!fetching && referenceId && (
          <>
            {fallbackNoticeKey && (
              <div className="col-12">
                <div className="alert alert-info mb-0">{t(fallbackNoticeKey)}</div>
              </div>
            )}

            <div className="col-md-3">
              <label className="form-label d-block text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.16em', color: 'var(--dim)', fontFamily: 'var(--font-sans)' }}>
                {t('system.branding.pageTexts.fields.logo.label')}
              </label>
              <ImageUpload
                currentUrl={formData.logo_url || null}
                baseUrl={`${API_URL}/uploads/product-logos`}
                uploadFn={ProductBrandingService.uploadLogo}
                onSuccess={handleLogoUploaded}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label d-block text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.16em', color: 'var(--dim)', fontFamily: 'var(--font-sans)' }}>
                {t('system.branding.pageTexts.fields.themeColour.label')}
              </label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  value={previewColour}
                  onChange={(e) => setFormData((prev) => ({ ...prev, theme_colour: e.target.value }))}
                />
                <Input
                  id="theme_colour"
                  name="theme_colour"
                  placeholder={DEFAULT_COLOUR}
                  value={formData.theme_colour}
                  error={!!errors.theme_colour}
                  errorMessage={errors.theme_colour}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md-5">
              <Input
                id="display_name"
                name="display_name"
                label={t('system.branding.pageTexts.fields.displayName.label')}
                placeholder={t('system.branding.pageTexts.fields.displayName.placeholder')}
                value={formData.display_name}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-8">
              <Input
                id="notes"
                name="notes"
                label={t('system.branding.pageTexts.fields.notes.label')}
                value={formData.notes}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label d-block text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.16em', color: 'var(--dim)', fontFamily: 'var(--font-sans)' }}>
                {t('system.branding.pageTexts.preview.title')}
              </label>
              <div
                className="d-flex align-items-center gap-2 rounded px-4 py-2"
                style={{ backgroundColor: previewColour, minHeight: 48 }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{ width: 28, height: 28, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}
                >
                  {previewLogoUrl ? (
                    <img src={previewLogoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span className="text-white small">{previewName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <span className="text-white fw-medium small">{previewName}</span>
              </div>
            </div>

            <div className="col-12 d-flex gap-2 justify-content-end">
              <Button type="submit" className="btn btn-primary" text={t('system.branding.pageTexts.buttons.save')} loading={loading} />
            </div>
          </>
        )}
      </form>
      <BrandingList refreshToken={listRefreshToken} />
    </div>
  );
};

const BrandingList: React.FC<{ refreshToken: number }> = ({ refreshToken }) => {
  const { t } = useTranslation();
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'scope' });
  const [rows, setRows] = useState<ProductBrandingRow[]>([]);
  const [scopeFilter, setScopeFilter] = useState<ProductBrandingScope | 'all'>('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    ProductBrandingService.list()
      .then((response: any) => setRows(response?.data ?? []))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  const filteredRows = scopeFilter === 'all' ? rows : rows.filter((row) => row.scope === scopeFilter);
  const sortedRows = useMemo(() => sortData(filteredRows, orderBy, order), [filteredRows, orderBy, order]);

  return (
    <div className="obs-table-card card mt-6">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="m-0">{t('system.branding.pageTexts.list.title')}</h6>
        <div className="btn-group btn-group-sm" role="group">
          {(['all', 'product', 'tenant'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`btn ${scopeFilter === option ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setScopeFilter(option)}
            >
              {option === 'all' ? t('system.branding.pageTexts.list.filterAll') : t(`system.branding.pageTexts.fields.scope.${option}`)}
            </button>
          ))}
        </div>
      </div>
      <hr className="m-0" style={{ borderColor: 'var(--line2)' }} />
      <div className="card-body p-0">
        <table className="table obs-table mb-0">
          <thead>
            <tr>
              {[
                { id: 'scope', label: t('system.branding.pageTexts.list.columns.scope') },
                { id: 'reference_name', label: t('system.branding.pageTexts.list.columns.reference') },
                { id: 'theme_colour', label: t('system.branding.pageTexts.list.columns.colour') },
                { id: 'display_name', label: t('system.branding.pageTexts.list.columns.displayName') },
                { id: 'updated_at', label: t('system.branding.pageTexts.list.columns.updated') },
              ].map((col) => {
                const isSorted = orderBy === col.id;
                return (
                  <th
                    key={col.id}
                    className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (orderBy === col.id) {
                        setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                      } else {
                        setOrderBy(col.id);
                        setOrder(SortOrder.asc);
                      }
                    }}
                  >
                    <span className="d-inline-flex align-items-center gap-1 w-100">
                      <span>{col.label}</span>
                      <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                        {isSorted ? (
                          order === SortOrder.asc ? (
                            <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                          ) : (
                            <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                          )
                        ) : (
                          <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                        )}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRow colSpan={5} />
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted p-4 text-center">{t('system.branding.pageTexts.list.empty')}</td>
              </tr>
            ) : (
              sortedRows.map((row: ProductBrandingRow) => (
                <tr key={row.id}>
                  <td>
                    <StatusBadge
                      label={t(`system.branding.pageTexts.fields.scope.${row.scope}`)}
                      colour={String(row.scope).includes('global') ? '#1976d2' : '#0288d1'}
                    />
                  </td>
                  <td>{row.reference_name}</td>
                  <td>
                    <span className="d-inline-flex align-items-center gap-2">
                      <span
                        className="rounded-circle d-inline-block"
                        style={{ width: 14, height: 14, backgroundColor: row.theme_colour }}
                      />
                      {row.theme_colour}
                    </span>
                  </td>
                  <td>{row.display_name ?? '—'}</td>
                  <td>{new Date(row.updated_at).toLocaleString()}</td>
                </tr>
              )))}
            </tbody>
          </table>
      </div>
    </div>
  );
};

export default BrandingForm;
